import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    DialogTitle,
    DialogContent,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Avatar,
    Tabs,
    Tab,
    TextField,
    InputAdornment,
    Button,
    Tooltip,
    Grid,
    Card,
    CardContent,
    LinearProgress,
    useTheme,
    Divider,
    Alert,
    Dialog,
    DialogActions,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Collapse,
    Badge,
    Menu,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import CommentIcon from '@mui/icons-material/Comment';
import ReviewsIcon from '@mui/icons-material/Reviews';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import * as XLSX from 'xlsx';
import { toast, ToastContainer } from 'react-toastify';

const StudentSubmissions = ({ task, onClose }) => {
    const [tabValue, setTabValue] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [students, setStudents] = useState([]);
    const [filteredStudents, setFilteredStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [reviewDialog, setReviewDialog] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [reviewStatus, setReviewStatus] = useState('');
    const [reviewComment, setReviewComment] = useState('');
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [documentViewer, setDocumentViewer] = useState({ open: false, url: '', type: '' });
    const theme = useTheme();

    // Mock data generator with enhanced submission data
    const generateMockStudents = () => {
        const mockStudents = [];
        const totalStudents = Math.floor(Math.random() * 30) + 25;
        const submittedCount = Math.floor(totalStudents * 0.7);

        const reviewStatuses = ['pending', 'approved', 'rejected'];
        const fileTypes = ['pdf', 'docx', 'jpg', 'png'];

        for (let i = 1; i <= totalStudents; i++) {
            const isSubmitted = i <= submittedCount;
            const submissionDate = isSubmitted
                ? new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000)
                : null;

            const fileType = fileTypes[Math.floor(Math.random() * fileTypes.length)];
            const reviewStatus = isSubmitted ? reviewStatuses[Math.floor(Math.random() * reviewStatuses.length)] : null;

            mockStudents.push({
                id: i,
                rollNumber: `ST${String(i).padStart(3, '0')}`,
                name: `Student ${i}`,
                email: `student${i}@school.edu`,
                isSubmitted,
                submissionDate,
                submissionTime: submissionDate ? submissionDate.toLocaleTimeString() : null,
                grade: isSubmitted && reviewStatus === 'approved' ? Math.floor(Math.random() * 41) + 60 : null,
                remarks: isSubmitted
                    ? ['Excellent work', 'Good effort', 'Needs improvement', 'Well done', 'On time'][Math.floor(Math.random() * 5)]
                    : null,
                submissionFile: isSubmitted ? `assignment_${i}.${fileType}` : null,
                fileUrl: isSubmitted ? `https://example.com/files/assignment_${i}.${fileType}` : null,
                fileType: isSubmitted ? fileType : null,
                fileSize: isSubmitted ? `${Math.floor(Math.random() * 5000) + 500}KB` : null,
                reviewStatus: reviewStatus,
                reviewComment: reviewStatus === 'rejected'
                    ? ['Please resubmit with corrections', 'Missing requirements', 'Format issues', 'Incomplete work'][Math.floor(Math.random() * 4)]
                    : reviewStatus === 'approved'
                        ? ['Great work!', 'Well done', 'Excellent submission', 'Perfect'][Math.floor(Math.random() * 4)]
                        : '',
                reviewedBy: reviewStatus !== 'pending' ? 'Admin User' : '',
                reviewedAt: reviewStatus !== 'pending' ? new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000) : null,
            });
        }

        return mockStudents;
    };

    useEffect(() => {
        setLoading(true);
        setTimeout(() => {
            const mockData = generateMockStudents();
            setStudents(mockData);
            setFilteredStudents(mockData);
            setLoading(false);
        }, 1000);
    }, [task]);

    useEffect(() => {
        const filtered = students.filter(student => {
            const matchesSearch = student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                student.rollNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
                student.email.toLowerCase().includes(searchQuery.toLowerCase());

            if (tabValue === 0) return matchesSearch; // All
            if (tabValue === 1) return matchesSearch && student.isSubmitted; // Submitted
            if (tabValue === 2) return matchesSearch && !student.isSubmitted; // Pending
            if (tabValue === 3) return matchesSearch && student.reviewStatus === 'pending'; // Needs Review
            if (tabValue === 4) return matchesSearch && student.reviewStatus === 'approved'; // Approved
            if (tabValue === 5) return matchesSearch && student.reviewStatus === 'rejected'; // Rejected

            return matchesSearch;
        });

        setFilteredStudents(filtered);
    }, [searchQuery, tabValue, students]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleOpenReview = (student) => {
        setSelectedStudent(student);
        setReviewStatus(student.reviewStatus || 'pending');
        setReviewComment(student.reviewComment || '');
        setReviewDialog(true);
    };

    const handleCloseReview = () => {
        setReviewDialog(false);
        setSelectedStudent(null);
        setReviewStatus('');
        setReviewComment('');
    };

    const handleSubmitReview = () => {
        if (!selectedStudent) return;

        // Update the student's review status
        const updatedStudents = students.map(student =>
            student.id === selectedStudent.id
                ? {
                    ...student,
                    reviewStatus,
                    reviewComment,
                    reviewedBy: 'Current User', // Replace with actual user
                    reviewedAt: new Date(),
                    grade: reviewStatus === 'approved' && !student.grade
                        ? Math.floor(Math.random() * 41) + 60
                        : student.grade
                }
                : student
        );

        setStudents(updatedStudents);
        setFilteredStudents(updatedStudents.filter(student => {
            const matchesSearch = student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                student.rollNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
                student.email.toLowerCase().includes(searchQuery.toLowerCase());

            if (tabValue === 0) return matchesSearch;
            if (tabValue === 1) return matchesSearch && student.isSubmitted;
            if (tabValue === 2) return matchesSearch && !student.isSubmitted;
            if (tabValue === 3) return matchesSearch && student.reviewStatus === 'pending';
            if (tabValue === 4) return matchesSearch && student.reviewStatus === 'approved';
            if (tabValue === 5) return matchesSearch && student.reviewStatus === 'rejected';

            return matchesSearch;
        }));

        toast.success(`Review submitted successfully for ${selectedStudent.name}!`);
        handleCloseReview();
    };

    const handleViewDocument = (fileUrl, fileType) => {
        setDocumentViewer({ open: true, url: fileUrl, type: fileType });
    };

    const handleDownloadDocument = (fileUrl, fileName) => {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const toggleRowExpansion = (studentId) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(studentId)) {
            newExpanded.delete(studentId);
        } else {
            newExpanded.add(studentId);
        }
        setExpandedRows(newExpanded);
    };

    const getFileIcon = (fileType) => {
        const type = fileType?.toLowerCase();
        if (['jpg', 'jpeg', 'png', 'gif'].includes(type)) return ImageIcon;
        if (type === 'pdf') return PictureAsPdfIcon;
        return InsertDriveFileIcon;
    };

    const handleDownloadReport = () => {
        const reportData = students.map(student => ({
            'Roll Number': student.rollNumber,
            'Student Name': student.name,
            'Email': student.email,
            'Status': student.isSubmitted ? 'Submitted' : 'Pending',
            'Submission Date': student.submissionDate ? student.submissionDate.toLocaleDateString() : 'N/A',
            'Submission Time': student.submissionTime || 'N/A',
            'File Name': student.submissionFile || 'N/A',
            'File Size': student.fileSize || 'N/A',
            'Review Status': student.reviewStatus || 'N/A',
            'Review Comment': student.reviewComment || 'N/A',
            'Reviewed By': student.reviewedBy || 'N/A',
            'Reviewed At': student.reviewedAt ? student.reviewedAt.toLocaleDateString() : 'N/A',
            'Grade': student.grade || 'N/A',
            'Remarks': student.remarks || 'N/A',
        }));

        const worksheet = XLSX.utils.json_to_sheet(reportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Submissions');
        const fileName = `${task?.title || 'Assignment'}_Submissions_Review_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
    };

    const getStatistics = () => {
        const total = students.length;
        const submitted = students.filter(s => s.isSubmitted).length;
        const pending = total - submitted;
        const needsReview = students.filter(s => s.reviewStatus === 'pending').length;
        const approved = students.filter(s => s.reviewStatus === 'approved').length;
        const rejected = students.filter(s => s.reviewStatus === 'rejected').length;
        const percentage = total > 0 ? Math.round((submitted / total) * 100) : 0;

        return { total, submitted, pending, percentage, needsReview, approved, rejected };
    };

    const stats = getStatistics();

    const tableHeaderStyle = {
        fontWeight: 600,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        fontSize: '0.95rem',
    };

    if (!task) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                    No task selected
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header */}
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pb: 2,
                borderBottom: `1px solid ${theme.palette.divider}`,
            }}>
                <Box>
                    <Typography variant="h5" component="div" sx={{ fontWeight: 600, mb: 1 }}>
                        Student Submissions & Review
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        {task.title} - {task.className} ({task.section})
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: theme.palette.grey[500] }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
                {/* Statistics Cards */}
                <Box sx={{ p: 3, pb: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={2}>
                            <Card sx={{ textAlign: 'center', bgcolor: theme.palette.primary.light }}>
                                <CardContent sx={{ py: 1.5 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white' }}>
                                        {stats.total}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'white' }}>
                                        Total Students
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <Card sx={{ textAlign: 'center', bgcolor: theme.palette.success.light }}>
                                <CardContent sx={{ py: 1.5 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white' }}>
                                        {stats.submitted}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'white' }}>
                                        Submitted
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <Card sx={{ textAlign: 'center', bgcolor: theme.palette.warning.light }}>
                                <CardContent sx={{ py: 1.5 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white' }}>
                                        {stats.pending}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'white' }}>
                                        Pending
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <Card sx={{ textAlign: 'center', bgcolor: theme.palette.info.light }}>
                                <CardContent sx={{ py: 1.5 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white' }}>
                                        {stats.needsReview}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'white' }}>
                                        Needs Review
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <Card sx={{ textAlign: 'center', bgcolor: theme.palette.success.main }}>
                                <CardContent sx={{ py: 1.5 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white' }}>
                                        {stats.approved}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'white' }}>
                                        Approved
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <Card sx={{ textAlign: 'center', bgcolor: theme.palette.error.light }}>
                                <CardContent sx={{ py: 1.5 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white' }}>
                                        {stats.rejected}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'white' }}>
                                        Rejected
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Progress Bar */}
                    <Box sx={{ mt: 2 }}>
                        <LinearProgress
                            variant="determinate"
                            value={stats.percentage}
                            sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: theme.palette.grey[200],
                            }}
                        />
                    </Box>
                </Box>

                <Divider />

                {/* Enhanced Tabs */}
                <Box sx={{ p: 3, pb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                            <Tab label={`All (${stats.total})`} icon={<PersonIcon />} iconPosition="start" />
                            <Tab label={`Submitted (${stats.submitted})`} icon={<CheckCircleIcon />} iconPosition="start" />
                            <Tab label={`Pending (${stats.pending})`} icon={<PendingIcon />} iconPosition="start" />
                            <Tab
                                label={
                                    <Badge badgeContent={stats.needsReview} color="error">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <ReviewsIcon />
                                            <span>Needs Review</span>
                                        </Box>
                                    </Badge>
                                }
                            />
                            <Tab label={`Approved (${stats.approved})`} icon={<ThumbUpIcon />} iconPosition="start" />
                            <Tab label={`Rejected (${stats.rejected})`} icon={<ThumbDownIcon />} iconPosition="start" />
                        </Tabs>

                        <Button
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadReport}
                            sx={{ textTransform: 'none' }}
                        >
                            Download Report
                        </Button>
                    </Box>

                    <TextField
                        fullWidth
                        placeholder="Search by name, roll number, or email..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ maxWidth: 400 }}
                    />
                </Box>

                {/* Enhanced Students Table */}
                <Box sx={{ flex: 1, px: 3, pb: 3 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <LinearProgress sx={{ width: '50%' }} />
                        </Box>
                    ) : (
                        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={tableHeaderStyle}>Roll No.</TableCell>
                                        <TableCell sx={tableHeaderStyle}>Student Name</TableCell>
                                        <TableCell sx={tableHeaderStyle}>Status</TableCell>
                                        <TableCell sx={tableHeaderStyle}>Submission Details</TableCell>
                                        <TableCell sx={tableHeaderStyle}>Document</TableCell>
                                        <TableCell sx={tableHeaderStyle}>Review Status</TableCell>
                                        <TableCell sx={tableHeaderStyle}>Actions</TableCell>
                                        <TableCell sx={tableHeaderStyle}>Expand</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredStudents.map((student) => (
                                        <React.Fragment key={student.id}>
                                            <TableRow hover>
                                                <TableCell>{student.rollNumber}</TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.9rem' }}>
                                                            {student.name.charAt(0)}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {student.name}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {student.email}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        icon={student.isSubmitted ? <AssignmentTurnedInIcon /> : <AccessTimeIcon />}
                                                        label={student.isSubmitted ? 'Submitted' : 'Pending'}
                                                        color={student.isSubmitted ? 'success' : 'warning'}
                                                        size="small"
                                                        variant={student.isSubmitted ? 'filled' : 'outlined'}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {student.submissionDate ? (
                                                        <Box>
                                                            <Typography variant="body2">
                                                                {student.submissionDate.toLocaleDateString()}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {student.submissionTime}
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            Not submitted
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {student.submissionFile ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            {React.createElement(getFileIcon(student.fileType), {
                                                                sx: { fontSize: 20, color: theme.palette.primary.main }
                                                            })}
                                                            <Box>
                                                                <Typography variant="body2" sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                    {student.submissionFile}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {student.fileSize}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            No file
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {student.reviewStatus ? (
                                                        <Chip
                                                            label={student.reviewStatus.charAt(0).toUpperCase() + student.reviewStatus.slice(1)}
                                                            color={
                                                                student.reviewStatus === 'approved' ? 'success' :
                                                                    student.reviewStatus === 'rejected' ? 'error' : 'warning'
                                                            }
                                                            size="small"
                                                            variant="filled"
                                                        />
                                                    ) : (
                                                        <Chip label="Not Reviewed" color="default" size="small" variant="outlined" />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        {student.submissionFile && (
                                                            <>
                                                                <Tooltip title="View Document">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleViewDocument(student.fileUrl, student.fileType)}
                                                                        sx={{ color: theme.palette.info.main }}
                                                                    >
                                                                        <VisibilityIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Download Document">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleDownloadDocument(student.fileUrl, student.submissionFile)}
                                                                        sx={{ color: theme.palette.primary.main }}
                                                                    >
                                                                        <FileDownloadIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </>
                                                        )}
                                                        {student.isSubmitted && (
                                                            <Tooltip title="Review Submission">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleOpenReview(student)}
                                                                    sx={{
                                                                        color: student.reviewStatus === 'pending' ? theme.palette.warning.main : theme.palette.success.main
                                                                    }}
                                                                >
                                                                    <ReviewsIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    {student.isSubmitted && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => toggleRowExpansion(student.id)}
                                                        >
                                                            {expandedRows.has(student.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                        </IconButton>
                                                    )}
                                                </TableCell>
                                            </TableRow>

                                            {/* Expanded Row for Additional Details */}
                                            <TableRow>
                                                <TableCell colSpan={8} sx={{ py: 0 }}>
                                                    <Collapse in={expandedRows.has(student.id)} timeout="auto" unmountOnExit>
                                                        <Box sx={{ m: 1, p: 2, bgcolor: theme.palette.grey[50], borderRadius: 1 }}>
                                                            <Typography variant="subtitle2" gutterBottom>
                                                                Additional Details
                                                            </Typography>
                                                            <Grid container spacing={2}>
                                                                <Grid item xs={12} sm={6}>
                                                                    <Typography variant="body2">
                                                                        <strong>Grade:</strong> {student.grade ? `${student.grade}%` : 'Not graded'}
                                                                    </Typography>
                                                                    <Typography variant="body2">
                                                                        <strong>Remarks:</strong> {student.remarks || 'No remarks'}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={12} sm={6}>
                                                                    <Typography variant="body2">
                                                                        <strong>Reviewed by:</strong> {student.reviewedBy || 'Not reviewed'}
                                                                    </Typography>
                                                                    <Typography variant="body2">
                                                                        <strong>Review Date:</strong> {student.reviewedAt ? student.reviewedAt.toLocaleDateString() : 'Not reviewed'}
                                                                    </Typography>
                                                                </Grid>
                                                                {student.reviewComment && (
                                                                    <Grid item xs={12}>
                                                                        <Typography variant="body2">
                                                                            <strong>Review Comment:</strong> {student.reviewComment}
                                                                        </Typography>
                                                                    </Grid>
                                                                )}
                                                            </Grid>
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {filteredStudents.length === 0 && !loading && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            No students found matching your search criteria.
                        </Alert>
                    )}
                </Box>
            </DialogContent>

            {/* Review Dialog */}
            <Dialog
                open={reviewDialog}
                onClose={handleCloseReview}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="h6">
                        Review Submission - {selectedStudent?.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {selectedStudent?.submissionFile}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Review Status</InputLabel>
                                <Select
                                    value={reviewStatus}
                                    label="Review Status"
                                    onChange={(e) => setReviewStatus(e.target.value)}
                                >
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="approved">Approved</MenuItem>
                                    <MenuItem value="rejected">Rejected</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                {selectedStudent?.submissionFile && (
                                    <>
                                        <Button
                                            variant="outlined"
                                            startIcon={<VisibilityIcon />}
                                            onClick={() => handleViewDocument(selectedStudent.fileUrl, selectedStudent.fileType)}
                                            size="small"
                                        >
                                            View
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<FileDownloadIcon />}
                                            onClick={() => handleDownloadDocument(selectedStudent.fileUrl, selectedStudent.submissionFile)}
                                            size="small"
                                        >
                                            Download
                                        </Button>
                                    </>
                                )}
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Review Comment"
                                value={reviewComment}
                                onChange={(e) => setReviewComment(e.target.value)}
                                placeholder="Enter your review comments here..."
                                variant="outlined"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 0 }}>
                    <Button onClick={handleCloseReview} variant="outlined">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmitReview}
                        variant="contained"
                        disabled={!reviewStatus}
                        startIcon={<CommentIcon />}
                    >
                        Submit Review
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Document Viewer Dialog */}
            <Dialog
                open={documentViewer.open}
                onClose={() => setDocumentViewer({ open: false, url: '', type: '' })}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3, height: '90vh' }
                }}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">Document Viewer</Typography>
                        <IconButton onClick={() => setDocumentViewer({ open: false, url: '', type: '' })}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {documentViewer.type === 'pdf' ? (
                        <iframe
                            src={documentViewer.url}
                            width="100%"
                            height="100%"
                            style={{ border: 'none', minHeight: '500px' }}
                            title="PDF Viewer"
                        />
                    ) : ['jpg', 'jpeg', 'png', 'gif'].includes(documentViewer.type) ? (
                        <img
                            src={documentViewer.url}
                            alt="Document"
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                    ) : (
                        <Box sx={{ textAlign: 'center', p: 4 }}>
                            <InsertDriveFileIcon sx={{ fontSize: 80, color: theme.palette.grey[400], mb: 2 }} />
                            <Typography variant="h6" color="text.secondary">
                                Preview not available for this file type
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Please download the file to view its contents.
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<FileDownloadIcon />}
                                onClick={() => handleDownloadDocument(documentViewer.url, 'document')}
                            >
                                Download File
                            </Button>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
            />
        </Box>
    );
};

export default StudentSubmissions;
